/**
 * Fitting rules for category-axis tick labels.
 *
 * A category axis gives each tick one slot, and a label must stay inside it or
 * it collides with its neighbours. The previous rule computed how many
 * characters fit and then applied `Math.max(6, fit)`, so a band narrower than
 * six characters was handed more text than it could hold: the "minimum"
 * guaranteed the overlap it looked like it prevented.
 *
 * The rules here never return a budget wider than the slot. When the slot is
 * too small to hold an identifiable label they reduce the number of labels
 * rather than the width of each one, because a two-character stub identifies
 * nothing. Full values stay reachable on hover via `bindTickTooltip`.
 */

/** Width of one character of 12px Work Sans, the tick-label font. */
export const AVG_CHAR_WIDTH_PX = 7;

/** White space kept between neighbouring labels so they read as separate words. */
export const LABEL_GUTTER_PX = 6;

/** Fewer characters than this is a stub, not an identifier. */
export const MIN_IDENTIFIABLE_CHARS = 4;

/** Budget for axes with no measurable band, such as continuous and time axes. */
export const UNBOUNDED_CHAR_LIMIT = 24;

/** Vertical room one rotated label occupies, including leading. */
export const ROTATED_LINE_HEIGHT_PX = 13;

/** Single-glyph ellipsis, so a truncated label costs one character, not three. */
export const ELLIPSIS = "…";

/**
 * Room below the axis line already claimed by the tick padding and the bottom
 * axis name, and so unavailable to rotated tick labels. See `addBottomAxisName`
 * in addLabels.ts for where that name is placed.
 */
export const X_AXIS_NAME_RESERVE_PX = 24;

/**
 * Largest share of the widget's height that rotated labels may claim. Rotated
 * labels are only worth their cost while the plot above them stays readable.
 */
export const MAX_ROTATED_LABEL_HEIGHT_RATIO = 0.3;

/**
 * Plot area that rotated labels may never eat into. On a short widget the
 * labels give way and shorten; the full value is still on hover, whereas a
 * squashed plot has nothing to fall back on.
 */
export const MIN_PLOT_HEIGHT_PX = 120;

export type CategoryAxisLabelPlan = {
  /** Longest rendered label, in characters. */
  charLimit: number;
  /** Render every nth category. 1 renders all of them. */
  tickInterval: number;
};

/**
 * Shorten `value` so the rendered string is at most `charLimit` characters,
 * counting the ellipsis. The previous implementation sliced to `limit - 2` and
 * appended three dots, which rendered `limit + 1` characters and so overspent
 * its own budget on every truncated label.
 */
export const truncateToLimit = (value: string, charLimit: number): string => {
  if (charLimit < 1) return "";
  if (value.length <= charLimit) return value;
  if (charLimit === 1) return ELLIPSIS;
  return `${value.slice(0, charLimit - 1)}${ELLIPSIS}`;
};

const charsThatFit = (widthPx: number) =>
  Math.floor((widthPx - LABEL_GUTTER_PX) / AVG_CHAR_WIDTH_PX);

const clampInterval = (interval: number, categoryCount: number) =>
  Math.min(Math.max(1, interval), Math.max(1, categoryCount));

/**
 * Plan horizontal labels. `slotWidthPx` is the distance between neighbouring
 * tick centres — `scaleBand.step()`, not `bandwidth()`, because labels are
 * centred on the band and it is the centre-to-centre distance that decides
 * whether they touch.
 */
export const planCategoryAxisLabels = ({
  categoryCount,
  slotWidthPx,
}: {
  categoryCount: number;
  slotWidthPx: number | null;
}): CategoryAxisLabelPlan => {
  if (!slotWidthPx || slotWidthPx <= 0) {
    return { charLimit: UNBOUNDED_CHAR_LIMIT, tickInterval: 1 };
  }

  const fit = charsThatFit(slotWidthPx);
  if (fit >= MIN_IDENTIFIABLE_CHARS) {
    return { charLimit: fit, tickInterval: 1 };
  }

  const widthForIdentifiableLabel =
    MIN_IDENTIFIABLE_CHARS * AVG_CHAR_WIDTH_PX + LABEL_GUTTER_PX;
  const tickInterval = clampInterval(
    Math.ceil(widthForIdentifiableLabel / slotWidthPx),
    categoryCount,
  );

  return {
    charLimit: Math.max(1, charsThatFit(slotWidthPx * tickInterval)),
    tickInterval,
  };
};

/**
 * Plan labels that will be rotated to vertical. Their length is bounded by the
 * room below the axis rather than by the band, and a rotated label needs only
 * one line height of band, so rotation fits far longer names than horizontal
 * text can — which is why it is the right presentation for long visit names.
 */
export const planRotatedCategoryAxisLabels = ({
  categoryCount,
  slotWidthPx,
  availableHeightPx,
}: {
  categoryCount: number;
  slotWidthPx: number | null;
  availableHeightPx: number;
}): CategoryAxisLabelPlan => {
  const tickInterval =
    slotWidthPx && slotWidthPx > 0
      ? clampInterval(
          Math.ceil(ROTATED_LINE_HEIGHT_PX / slotWidthPx),
          categoryCount,
        )
      : 1;

  return {
    charLimit: Math.max(1, Math.floor(availableHeightPx / AVG_CHAR_WIDTH_PX)),
    tickInterval,
  };
};

/**
 * Bottom margin needed to show rotated labels of `maxCategoryLabelLength`
 * characters. The base margin is kept as the floor and only grown by what the
 * labels need beyond it, capped so the plot area stays usable — a fixed margin
 * would silently clip rotated labels against the widget edge instead.
 */
export const rotatedLabelMarginBottom = ({
  baseMarginBottom,
  maxCategoryLabelLength,
  containerHeightPx,
  reservedHeightPx,
}: {
  baseMarginBottom: number;
  maxCategoryLabelLength: number;
  containerHeightPx: number;
  /** Height already committed to titles and padding, outside plot and margin. */
  reservedHeightPx: number;
}): number => {
  const roomInBaseMargin = Math.max(
    0,
    baseMarginBottom - X_AXIS_NAME_RESERVE_PX,
  );
  const wantedPx = Math.max(0, maxCategoryLabelLength) * AVG_CHAR_WIDTH_PX;
  const affordablePx = Math.min(
    containerHeightPx * MAX_ROTATED_LABEL_HEIGHT_RATIO,
    // Whatever is left once the plot keeps its floor.
    containerHeightPx -
      reservedHeightPx -
      baseMarginBottom -
      MIN_PLOT_HEIGHT_PX,
  );

  const extraPx = Math.max(
    0,
    Math.min(wantedPx, Math.max(0, affordablePx)) - roomInBaseMargin,
  );

  return baseMarginBottom + extraPx;
};
